import { rowsHaveAllColumns, rowsHaveColumn } from './columnGuards';
import { applyComputedColumns } from './computedColumns';
import { normalizeRowsToShare } from './normalizeRowsToShare';
import { pivotLongFormToWide } from './pivotLongFormToWide';
import { sortRows } from './sortRows';
import { topN } from './topN';
import { unpivotWideToLong } from './unpivot';
import type {
  ComputedColumnsConfig,
  PivotConfig,
  SortConfig,
  StepApplyResult,
  TopNConfig,
  TransformRow,
  TransformStep,
  TransformStepKind,
  UnpivotConfig,
} from './types';

type StepHandler = (rows: TransformRow[], config: unknown) => StepApplyResult;

function applyPivotStep(
  rows: TransformRow[],
  config: PivotConfig,
): StepApplyResult {
  const required = [config.dimension, config.indexBy, config.measure] as const;
  if (!rowsHaveAllColumns(rows, required)) {
    const missing = required.filter((col) => !rowsHaveColumn(rows, col));
    return {
      rows,
      warnings: [`pivot step skipped: missing column(s) ${missing.join(', ')}`],
    };
  }

  let wide = pivotLongFormToWide(rows, config) as TransformRow[];
  const warnings: string[] = [];

  if (config.normalize === 'row-share') {
    if (config.valueMap) {
      const seriesKeys = Object.values(config.valueMap);
      wide = normalizeRowsToShare(wide, seriesKeys) as TransformRow[];
    } else {
      warnings.push(
        "pivot row-share normalization skipped: 'normalize' requires 'valueMap'",
      );
    }
  }

  return { rows: wide, warnings };
}

function applySortStep(
  rows: TransformRow[],
  config: SortConfig,
): StepApplyResult {
  if (config.sortBySeries && !rowsHaveColumn(rows, config.sortBySeries)) {
    return {
      rows,
      warnings: [`sort step skipped: missing column '${config.sortBySeries}'`],
    };
  }

  return { rows: sortRows(rows, config), warnings: [] };
}

function applyTopNStep(
  rows: TransformRow[],
  config: TopNConfig,
): StepApplyResult {
  return { rows: topN(rows, config), warnings: [] };
}

function applyUnpivotStep(
  rows: TransformRow[],
  config: UnpivotConfig,
): StepApplyResult {
  return unpivotWideToLong(rows, config);
}

function applyComputedColumnsStep(
  rows: TransformRow[],
  config: ComputedColumnsConfig,
): StepApplyResult {
  return applyComputedColumns(rows, config);
}

// StepHandler uses `config: unknown`; casts are safe because applyTransformStep
// dispatches via the TransformStep discriminated union (kind ↔ config pairing).
export const TRANSFORM_REGISTRY: Record<TransformStepKind, StepHandler> = {
  pivot: (rows, config) => applyPivotStep(rows, config as PivotConfig),
  sort: (rows, config) => applySortStep(rows, config as SortConfig),
  topN: (rows, config) => applyTopNStep(rows, config as TopNConfig),
  unpivot: (rows, config) => applyUnpivotStep(rows, config as UnpivotConfig),
  computedColumns: (rows, config) =>
    applyComputedColumnsStep(rows, config as ComputedColumnsConfig),
};

export function isKnownTransformStepKind(
  kind: string,
): kind is TransformStepKind {
  return (
    kind === 'pivot' ||
    kind === 'sort' ||
    kind === 'topN' ||
    kind === 'unpivot' ||
    kind === 'computedColumns'
  );
}

export function applyTransformStep(
  rows: TransformRow[],
  step: TransformStep,
): StepApplyResult {
  return TRANSFORM_REGISTRY[step.kind](rows, step.config);
}
